import { Check } from 'lucide-react'
import { useRef, useState, type KeyboardEvent } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { TAG_COLORS, isTagColor, tagVar } from '@/components/ui/tag-colors'

/** Arrow keys walk the swatches in order, wrapping at both ends (ARIA radio group). */
const STEP: Record<string, number> = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }

/**
 * Pick one of the twelve palette colours (design §4.8). The swatches form one stop in
 * the tab order (roving tabindex): Tab enters on the current colour, the arrow keys
 * move on. Moving is not choosing: a choice saves, so it takes Enter or Space.
 */
export function ColorSwatchPicker({
  value,
  onChange,
  label = 'Kleur',
}: {
  value: string
  onChange: (color: string) => void
  label?: string
}) {
  const [open, setOpen] = useState(false)
  // The swatch the arrow keys last moved to; null = the current colour (or the first
  // swatch when the colour is not in the palette).
  const [moved, setMoved] = useState<string | null>(null)
  const swatches = useRef(new Map<string, HTMLButtonElement>())
  const tabStop = moved ?? (isTagColor(value) ? value : TAG_COLORS[0])

  function changeOpen(next: boolean) {
    setOpen(next)
    if (!next) setMoved(null)
  }

  function move(event: KeyboardEvent, index: number) {
    const step = STEP[event.key]
    if (!step) return
    event.preventDefault()
    const next = TAG_COLORS[(index + step + TAG_COLORS.length) % TAG_COLORS.length]
    setMoved(next)
    swatches.current.get(next)?.focus()
  }

  return (
    <Popover open={open} onOpenChange={changeOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`${label}: ${value}`}
          className="inline-flex h-8 items-center gap-2 rounded-md border border-border bg-background px-2 text-sm hover:bg-muted"
        >
          <span
            aria-hidden
            className="size-3 rounded-full"
            style={{ backgroundColor: tagVar(value, 'dot') }}
          />
          {value}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-2">
        <div role="radiogroup" aria-label={label} className="grid grid-cols-3 gap-1">
          {TAG_COLORS.map((color, index) => (
            <button
              key={color}
              ref={(node) => {
                if (node) swatches.current.set(color, node)
                else swatches.current.delete(color)
              }}
              type="button"
              role="radio"
              aria-checked={color === value}
              tabIndex={color === tabStop ? 0 : -1}
              onKeyDown={(event) => move(event, index)}
              onClick={() => {
                onChange(color)
                changeOpen(false)
              }}
              className="flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
            >
              <span
                aria-hidden
                className="size-3 shrink-0 rounded-full"
                style={{ backgroundColor: tagVar(color, 'dot') }}
              />
              {color}
              {color === value ? <Check className="ml-auto size-3.5" aria-hidden /> : null}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
